import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { ExampleNotePrompt, ExampleNoteReview } from '@/hooks/useExampleNotePrompt';
import { CircleCheck, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

const PII_TYPE_LABELS: Record<string, string> = {
  NAME: 'nöfn',
  KENNITALA: 'kennitölur',
  PHONE: 'símanúmer',
  EMAIL: 'netföng',
  ADDRESS: 'heimilisföng',
  OCCUPATION: 'starfsheiti',
};

const summarizeReplaced = (replaced: Record<string, number>): string =>
  Object.entries(replaced)
    .map(([type, count]) => `${PII_TYPE_LABELS[type] ?? 'annað'} (${count})`)
    .join(', ');

interface ExampleNotePromptDialogProps {
  /** The dialog is open while this is set. Give the dialog `key={prompt?.key}` so each prompt starts fresh. */
  prompt: ExampleNotePrompt | null;
  isSaving: boolean;
  onStartReview: () => void;
  onSave: (text: string, dontShowAgain: boolean) => void;
  onClose: (dontShowAgain: boolean) => void;
}

export const ExampleNotePromptDialog = ({
  prompt,
  isSaving,
  onStartReview,
  onSave,
  onClose,
}: ExampleNotePromptDialogProps) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  return (
    <AlertDialog
      open={prompt !== null}
      onOpenChange={(next) => {
        if (!next && !isSaving) onClose(dontShowAgain);
      }}
    >
      {prompt && (
        <AlertDialogContent
          className={`max-h-[90dvh] overflow-y-auto ${prompt.step === 'ask' ? 'max-w-md' : 'max-w-2xl'}`}
        >
          {prompt.step === 'ask' ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Vista dæmi um nótu?</AlertDialogTitle>
                <AlertDialogDescription>
                  Í sniðmátinu <span className="font-medium text-foreground">{prompt.templateName}</span> sem er valið
                  er ekki vistað dæmi um nótu. Viltu vista þessa nótu sem það dæmi?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="example-note-dont-show-again"
                  checked={dontShowAgain}
                  onCheckedChange={(checked) => setDontShowAgain(checked === true)}
                />
                <Label htmlFor="example-note-dont-show-again" className="font-normal text-sm">
                  Ekki sýna aftur
                </Label>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Nei</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(event) => {
                    event.preventDefault();
                    onStartReview();
                  }}
                >
                  Já
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <ReviewStep
              templateName={prompt.templateName}
              review={prompt.step === 'review' ? prompt.review : null}
              isSaving={isSaving}
              onSave={(text) => onSave(text, dontShowAgain)}
            />
          )}
        </AlertDialogContent>
      )}
    </AlertDialog>
  );
};

interface ReviewStepProps {
  templateName: string;
  /** Null while the suggestion is being made. */
  review: ExampleNoteReview | null;
  isSaving: boolean;
  onSave: (text: string) => void;
}

const ReviewStep = ({ templateName, review, isSaving, onSave }: ReviewStepProps) => (
  <>
    <AlertDialogHeader>
      <AlertDialogTitle>Farðu yfir dæmið áður en það er vistað</AlertDialogTitle>
      <AlertDialogDescription>
        Dæmið stýrir framsetningu allra nótna úr sniðmátinu{' '}
        <span className="font-medium text-foreground">{templateName}</span>. Það má ekki innihalda upplýsingar um þennan
        sjúkling.
      </AlertDialogDescription>
    </AlertDialogHeader>
    {review ? (
      <ReviewEditor review={review} isSaving={isSaving} onSave={onSave} />
    ) : (
      <>
        <p className="text-muted-foreground text-sm">Leita að persónuupplýsingum…</p>
        <AlertDialogFooter>
          <AlertDialogCancel>Hætta við</AlertDialogCancel>
          <AlertDialogAction disabled>Vista dæmi</AlertDialogAction>
        </AlertDialogFooter>
      </>
    )}
  </>
);

interface ReviewEditorProps {
  review: ExampleNoteReview;
  isSaving: boolean;
  onSave: (text: string) => void;
}

// Mounted once the suggestion exists, so the draft starts from it without a sync effect.
const ReviewEditor = ({ review, isSaving, onSave }: ReviewEditorProps) => {
  const form = useForm({
    defaultValues: { draft: review.text, checkedByHand: false },
    mode: 'onChange',
  });
  const summary = summarizeReplaced(review.replaced);
  const scanSucceeded = review.complete && !review.failed;
  const StatusIcon = scanSucceeded ? CircleCheck : TriangleAlert;

  return (
    <>
      <div className="space-y-2">
        <div
          role={scanSucceeded ? 'status' : 'alert'}
          className={`flex items-start gap-3 rounded-lg border p-3 text-sm ${
            scanSucceeded
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-destructive/30 bg-destructive/5 text-destructive'
          }`}
        >
          <StatusIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <div className="space-y-1">
            <p className="font-medium">
              {scanSucceeded
                ? 'Sjálfvirk leit að persónuupplýsingum tókst'
                : review.failed
                  ? 'Sjálfvirk leit að persónuupplýsingum tókst ekki'
                  : 'Sjálfvirk leit tókst aðeins að hluta'}
            </p>
            <p>
              {scanSucceeded
                ? 'Farðu yfir textann áður en þú vistar.'
                : review.failed
                  ? 'Textinn er óbreyttur. Fjarlægðu allar persónuupplýsingar handvirkt.'
                  : 'Ekki tókst að ljúka öllum skrefum leitarinnar. Farðu sérstaklega vel yfir textann.'}
            </p>
          </div>
        </div>
        {!review.failed && (
          <p className="text-sm">
            {summary ? `Skipt var út fyrir tilbúin gildi: ${summary}.` : 'Engar persónuupplýsingar fundust sjálfkrafa.'}
          </p>
        )}
        <Label htmlFor="example-note-draft">Dæmi um nótu</Label>
        <Textarea
          id="example-note-draft"
          {...form.register('draft', { validate: (value) => !!value.trim() })}
          className="min-h-64 font-mono text-sm"
        />
        <p className="text-muted-foreground text-xs">
          Fjarlægðu einnig dagsetningar, staði og sjaldgæf atriði sem gætu bent á sjúklinginn.
        </p>
        <div className="flex items-center gap-2">
          <Controller
            name="checkedByHand"
            control={form.control}
            rules={{ validate: (value) => value }}
            render={({ field }) => (
              <Checkbox
                id="example-note-checked-by-hand"
                checked={field.value}
                onCheckedChange={(value) => field.onChange(value === true)}
              />
            )}
          />
          <Label htmlFor="example-note-checked-by-hand" className="font-normal text-sm">
            Ég hef farið yfir textann og fjarlægt persónuupplýsingar
          </Label>
        </div>
      </div>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={isSaving}>Hætta við</AlertDialogCancel>
        <AlertDialogAction
          disabled={isSaving || !form.formState.isValid}
          onClick={(event) => {
            // Keep the dialog open until the save finishes.
            event.preventDefault();
            void form.handleSubmit(({ draft }) => onSave(draft))();
          }}
        >
          {isSaving ? 'Vista…' : 'Vista dæmi'}
        </AlertDialogAction>
      </AlertDialogFooter>
    </>
  );
};
